<template>
  <div class="v2-cart-errors">
    <!-- Cancelled products -->
    <V2CartErrorPopup
      v-if="showCancelledErrorToast"
      :error-title="errorTitle[ERRORS.CANCELLED]"
      remove-all-btn-title="Remove all cancelled items"
      :is-removing-items="isRemovingCancelledItems"
      @on-cross-icon-click="onClickCrossIcon(ERRORS.CANCELLED)"
      @on-remove-all-click="removeAllCancelledItems"
    />
    <!-- Out of stock products -->
    <V2CartErrorPopup
      v-if="showOutOfStockErrorToast"
      :error-title="errorTitle[ERRORS.OUT_OF_STOCK]"
      remove-all-btn-title="Remove all out of stock items"
      :is-removing-items="isRemovingOutOfStockItems"
      @on-cross-icon-click="onClickCrossIcon(ERRORS.OUT_OF_STOCK)"
      @on-remove-all-click="removeAllOutOfStockItems"
    />
    <!-- Zero quantity products -->
    <V2CartErrorPopup
      v-if="showZeroQtyErrorToast"
      :error-title="errorTitle[ERRORS.ZERO_QTY]"
      remove-all-btn-title="Remove all items with 0 quantities"
      :is-removing-items="isRemovingZeroQtyItems"
      :should-show-remove-all-btn="!isBrand"
      @on-cross-icon-click="onClickCrossIcon(ERRORS.ZERO_QTY)"
      @on-remove-all-click="removeAllZeroQtyItems"
    />
    <!-- Decimal quantity products -->
    <V2CartErrorPopup
      v-if="showDecimalQtyErrorToast"
      :error-title="errorTitle[ERRORS.DECIMAL_QTY]"
      remove-all-btn-title="Remove all items with decimal quantities"
      :should-show-remove-all-btn="false"
      @on-cross-icon-click="onClickCrossIcon(ERRORS.DECIMAL_QTY)"
    />
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import constants from '@/constants'
import {
  LOAD_CARTS,
  PUSH_EXPANDED_CARTS,
  UPDATE_CART_ERRORS,
  DELETE_PRODUCTS_BY_CART_ID,
  SET_CART_HAS_ERRORS,
} from '@/store/modules/checkout-v2.module'
import { mapState } from 'vuex'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import V2CartErrorPopup from './V2CartErrorPopup.vue'

const { PRODUCT_STATUS } = c

const ERRORS = {
  CANCELLED: 'cancelled',
  OUT_OF_STOCK: 'outofstock',
  ZERO_QTY: 'zeroqty',
  DECIMAL_QTY: 'decimalQty',
}

export default {
  name: 'V2CartErrors',
  components: { V2CartErrorPopup },
  props: {
    cartId: {
      type: String,
      default: '',
    },
    cartProducts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      cancelledItemsCount: 0,
      outOfStockItemsCount: 0,
      zeroQtyItemsCount: 0,
      decimalQtyItemsCount: 0,
      showCancelledErrorToast: false,
      showOutOfStockErrorToast: false,
      showZeroQtyErrorToast: false,
      showDecimalQtyErrorToast: false,
      cancelledProductIds: [],
      outOfStockProductIds: [],
      zeroQtyProductIds: [],
      decimalQtyProductIds: [],
      isRemovingCancelledItems: false,
      isRemovingOutOfStockItems: false,
      isRemovingZeroQtyItems: false,
      ERRORS,
    }
  },
  computed: {
    ...mapState({
      carts: state => state.checkout.carts,
    }),
    isBrand() {
      return this.$store.getters.entityType === 'brand'
    },
    errorTitle() {
      return {
        [ERRORS.CANCELLED]: this.cancelledItemsCount > 1
          ? `${this.cancelledItemsCount} items are cancelled.`
          : `${this.cancelledItemsCount} item is cancelled.`,
        [ERRORS.OUT_OF_STOCK]: this.outOfStockItemsCount > 1
          ? `${this.outOfStockItemsCount} items are out of stock.`
          : `${this.outOfStockItemsCount} item is out of stock.`,
        [ERRORS.ZERO_QTY]: this.zeroQtyItemsCount > 1
          ? `${this.zeroQtyItemsCount} items have 0 quantities.`
          : `${this.zeroQtyItemsCount} item has 0 quantities.`,
        [ERRORS.DECIMAL_QTY]: this.decimalQtyItemsCount > 1
          ? `${this.decimalQtyItemsCount} items have decimal quantities.`
          : `${this.decimalQtyItemsCount} item has decimal quantity.`,
      }
    },
  },
  watch: {
    cartProducts: {
      handler() {
        this.loadErrors()
      },
      deep: true,
    },
  },
  created() {
    this.loadErrors()
  },
  methods: {
    loadErrors() {
      this.resetCounters()
      this.cartProducts.forEach(product => {
        if (product.status === PRODUCT_STATUS.CANCELLED.VALUE) {
          this.cancelledItemsCount++
          this.cancelledProductIds.push(product.productId)
        } else if (product?.hasError) {
          this.outOfStockItemsCount++
          this.outOfStockProductIds.push(product.productId)
        } else if (!product?.lineTotalQuantity) {
          this.zeroQtyItemsCount++
          this.zeroQtyProductIds.push(product.productId || product._id)
        } else if (product.lineTotalQuantity % 1 !== 0) {
          this.decimalQtyItemsCount++
          this.decimalQtyProductIds.push(product.productId)
        }
      })
      this.toggleErrorToast()
    },
    resetCounters() {
      this.cancelledItemsCount = 0
      this.outOfStockItemsCount = 0
      this.zeroQtyItemsCount = 0
      this.decimalQtyItemsCount = 0
      this.cancelledProductIds = []
      this.outOfStockProductIds = []
      this.zeroQtyProductIds = []
      this.decimalQtyProductIds = []
    },
    resetLoaders() {
      this.isRemovingCancelledItems = false
      this.isRemovingOutOfStockItems = false
      this.isRemovingZeroQtyItems = false
    },
    toggleErrorToast() {
      this.showCancelledErrorToast = !!this.cancelledItemsCount
      this.showOutOfStockErrorToast = !!this.outOfStockItemsCount
      this.showZeroQtyErrorToast = !!this.zeroQtyItemsCount
      this.showDecimalQtyErrorToast = !!this.decimalQtyItemsCount
      const isCartHasAnError = this.showCancelledErrorToast
        || this.showOutOfStockErrorToast
        || this.showZeroQtyErrorToast
        || this.showDecimalQtyErrorToast
      if (isCartHasAnError) {
        this.$store.commit(PUSH_EXPANDED_CARTS, this.cartId)
      }
      this.validateAllCartProducts()
    },
    removeAllCancelledItems() {
      if (this.isRemovingCancelledItems) return
      this.$emit('on-click-cart-error-button', this.errorTitle[ERRORS.CANCELLED])
      this.isRemovingCancelledItems = true
      this.removeMultipleProducts(this.cancelledProductIds)
    },
    removeAllOutOfStockItems() {
      if (this.isRemovingOutOfStockItems) return
      this.$emit('on-click-cart-error-button', this.errorTitle[ERRORS.OUT_OF_STOCK])
      this.isRemovingOutOfStockItems = true
      this.removeMultipleProducts(this.outOfStockProductIds)
    },
    removeAllZeroQtyItems() {
      if (this.isRemovingZeroQtyItems) return
      this.$emit('on-click-cart-error-button', this.errorTitle[ERRORS.ZERO_QTY])
      if (this.isBrand) {
        this.$emit('update:remove-products-brand-cart', this.zeroQtyProductIds)
        this.$store.commit(UPDATE_CART_ERRORS)
        this.loadErrors()
        this.isRemovingZeroQtyItems = true
        return
      }
      this.isRemovingZeroQtyItems = true
      this.removeMultipleProducts(this.zeroQtyProductIds)
      analytics.track(constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES.RETAILER_CLEAR_PRODUCTS_WITH_0_QTY, {
        cartId: this.cartId,
        productIds: this.zeroQtyProductIds,
        errorItems: this.zeroQtyItemsCount,
      })
    },
    removeMultipleProducts(productIds = []) {
      this.$store.dispatch(DELETE_PRODUCTS_BY_CART_ID, { cartId: this.cartId, productIds })
        .then(res => {
          apiToastSuccess(res.data.message)
          this.$store.dispatch(LOAD_CARTS)
          this.$store.commit(UPDATE_CART_ERRORS)
          this.resetLoaders()
        })
        .catch(err => {
          apiToastError(err)
          this.resetLoaders()
        })
    },
    onClickCrossIcon(type) {
      this.$emit('on-close-cart-error-popup', this.errorTitle[type])
      const trackData = {
        cartId: this.cartId,
        errorType: type,
        errorMessage: this.errorTitle[type],
      }
      analytics.track(
        constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES.RETAILER_CLOSES_VALIDATION_ERROR_MODAL,
        trackData,
      )
      switch (type) {
        case ERRORS.CANCELLED:
          this.showCancelledErrorToast = false
          break
        case ERRORS.OUT_OF_STOCK:
          this.showOutOfStockErrorToast = false
          break
        case ERRORS.ZERO_QTY:
          this.showZeroQtyErrorToast = false
          break
        case ERRORS.DECIMAL_QTY:
          this.showDecimalQtyErrorToast = false
          break
        default:
          break
      }
    },
    validateAllCartProducts() {
      const allCartProducts = this.carts.flatMap(cart => cart.products)
      const errorCounts = {
        cancelled: 0,
        outOfStock: 0,
        zeroQty: 0,
        decimalQty: 0,
      }
      allCartProducts.forEach(product => {
        if (product.status === PRODUCT_STATUS.CANCELLED.VALUE) {
          errorCounts.cancelled++
        } else if (product?.hasError) {
          errorCounts.outOfStock++
        } else if (!product?.lineTotalQuantity) {
          errorCounts.zeroQty++
        } else if (product.lineTotalQuantity % 1 !== 0) {
          errorCounts.decimalQty++
        }
      })
      this.commitErrorsToStore(errorCounts)
    },
    commitErrorsToStore(errorCounts) {
      const hasErrors = errorCounts.cancelled > 0
        || errorCounts.outOfStock > 0
        || errorCounts.zeroQty > 0
        || errorCounts.decimalQty > 0
      this.$store.commit(SET_CART_HAS_ERRORS, hasErrors)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-cart-errors {
  display: flex;
  flex-direction: column;
}
</style>
